import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { FEATURES } from '@academy/config'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { Card, CardBody, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Select } from '../components/Field.js'
import { FeatureGate } from '../components/FeatureGate.js'

export const Route = createFileRoute('/_app/tests/')({
  component: () => (
    <FeatureGate feature={FEATURES.PERFORMANCE}>
      <TestsPage />
    </FeatureGate>
  ),
})

interface TestRow {
  id: string
  classId: string
  className: string
  name: string
  type: string
  subject: string | null
  maxMarks: number
  testDate: string
  createdAt: string
}

interface ClassRow {
  id: string
  name: string
  isActive: boolean
}

const TEST_TYPES = [
  'slip_test',
  'weekly_test',
  'unit_test',
  'mock',
  'final',
] as const

function TestsPage() {
  const qc = useQueryClient()

  const tests = useQuery({
    queryKey: ['tests'],
    queryFn: async () => {
      const res = await api<TestRow[]>('/api/v1/tests')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const classes = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [draft, setDraft] = useState({
    classId: '',
    name: '',
    type: 'unit_test' as (typeof TEST_TYPES)[number],
    subject: '',
    maxMarks: 100,
    testDate: new Date().toISOString().slice(0, 10),
  })
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const res = await api<TestRow>('/api/v1/tests', {
        method: 'POST',
        body: JSON.stringify({
          ...draft,
          subject: draft.subject || undefined,
        }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tests'] })
      setDraft({
        classId: '',
        name: '',
        type: 'unit_test',
        subject: '',
        maxMarks: 100,
        testDate: new Date().toISOString().slice(0, 10),
      })
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    create.mutate()
  }

  const columns: Column<TestRow>[] = [
    {
      key: 'name',
      header: 'Test',
      render: (t) => (
        <div>
          <Link
            to="/tests/$id"
            params={{ id: t.id }}
            className="font-medium text-fg hover:text-[var(--color-primary)]"
          >
            {t.name}
          </Link>
          <p className="text-xs text-fg-subtle">
            {t.type.replace('_', ' ')}
            {t.subject ? ` · ${t.subject}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'class',
      header: 'Class',
      render: (t) => <span className="text-fg-muted">{t.className}</span>,
    },
    {
      key: 'date',
      header: 'Date',
      align: 'right',
      render: (t) => (
        <span className="text-sm text-fg-muted">
          {new Date(t.testDate).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'max',
      header: 'Max',
      align: 'right',
      render: (t) => (
        <span className="font-mono tabular-nums text-fg-muted">{t.maxMarks}</span>
      ),
    },
    {
      key: 'go',
      header: '',
      align: 'right',
      render: (t) => (
        <Link
          to="/tests/$id"
          params={{ id: t.id }}
          className="text-xs text-fg-muted hover:text-fg"
        >
          Enter scores →
        </Link>
      ),
    },
  ]

  const activeClasses = classes.data?.filter((c) => c.isActive) ?? []

  return (
    <div className="space-y-8">
      <PageHeader
        title="Tests"
        description="Slip tests, weekly tests, mocks. Create a test, then enter scores in bulk."
      />

      <Section title="Create a test">
        <Card>
          <CardBody>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Class" required className="md:col-span-1">
                <Select
                  value={draft.classId}
                  onChange={(e) => setDraft({ ...draft, classId: e.target.value })}
                  required
                >
                  <option value="">Select…</option>
                  {activeClasses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Name" required>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Mid-term Maths"
                  required
                />
              </Field>
              <Field label="Type" required>
                <Select
                  value={draft.type}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      type: e.target.value as (typeof TEST_TYPES)[number],
                    })
                  }
                >
                  {TEST_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replace('_', ' ')}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Subject" optional>
                <Input
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  placeholder="Mathematics"
                />
              </Field>
              <Field label="Max marks" required>
                <Input
                  type="number"
                  min="1"
                  max="1000"
                  value={draft.maxMarks}
                  onChange={(e) => setDraft({ ...draft, maxMarks: Number(e.target.value) })}
                  numeric
                  required
                />
              </Field>
              <Field label="Test date" required>
                <Input
                  type="date"
                  value={draft.testDate}
                  onChange={(e) => setDraft({ ...draft, testDate: e.target.value })}
                  required
                />
              </Field>
            </form>
          </CardBody>
          <CardFooter className="flex items-center justify-between">
            {error ? <p className="text-sm text-danger">{error}</p> : <span />}
            <Button
              onClick={() => create.mutate()}
              disabled={!draft.classId || !draft.name || create.isPending}
              loading={create.isPending}
            >
              Create test
            </Button>
          </CardFooter>
        </Card>
      </Section>

      <Section title="All tests" description={`${tests.data?.length ?? 0} total`}>
        <DataTable
          columns={columns}
          rows={tests.data ?? []}
          rowKey={(t) => t.id}
          loading={tests.isLoading}
          empty="No tests yet. Create your first one above."
        />
      </Section>
    </div>
  )
}
